import type { ResolvedField } from "@/features/customField/api/useResolveCustomDefaults";

/**
 * `customInputScopes` alignment for `PrefrenceFormRenderer` and every picker
 * that shares its array (course Intake Month, deal preferences…). Each
 * selector binds `customInputScopes.<index>` by the field's POSITION in the
 * `/specifics` field list, so the array must be kept in exactly that order.
 *
 * SHAPE: `[ ...one slot per field in `fields` order, ...every other entry ]`.
 * The tail is untouched and keeps its order. It exists because other cards
 * on the same form own entries for fields this renderer does not render and
 * find them BY ID — the course form's Revenue & Commissions card appends one
 * per NUMBERS field (and re-appends whenever they go missing). An aligner
 * that dropped them fought that card forever: drop → re-append → drift →
 * drop… = React #185 on Edit Course (2026-09-08, reached production).
 * Keeping the tail makes both owners' views of the array a fixed point.
 */
export function buildAlignedScopes(
  fields: any[],
  currentScopes: any[],
  resolvedFields: ResolvedField[] = []
) {
  const safeScopes = (currentScopes ?? []).filter(
    (s): s is Record<string, unknown> => s != null
  );
  const resolvedMap = new Map(
    resolvedFields.map((rf) => [rf.customInputId, rf])
  );
  const fieldIds = new Set(fields.map((f: any) => Number(f.id)));

  const head = fields.map((field: any) => {
    const existing = safeScopes.find(
      (s: any) => Number(s?.customInputId) === Number(field.id)
    );

    // Locked field (auto-fill with exactly one option): the slot ALWAYS holds
    // that option. The selector renders it as a read-only box showing the
    // resolved label, so the form value has to be the same value — a pick
    // made before the lock arrived (e.g. the Attach Course modal's Country,
    // which follows the left-hand Country filter and can change after the
    // course was picked) would otherwise be saved while the screen shows
    // something else. Still a fixed point: the second pass sees the same
    // option and `scopesEquivalent` stops the write.
    const resolved = resolvedMap.get(field.id);
    if (resolved?.autoFill && resolved.resolvedOptions.length === 1) {
      const lockedId = String(resolved.resolvedOptions[0].id);
      const current = Array.isArray(existing?.optionIds)
        ? (existing!.optionIds as unknown[]).map(String)
        : [];
      if (current.length === 1 && current[0] === lockedId) return existing!;
      return { customInputId: field.id, optionIds: [lockedId] };
    }

    if (existing) return existing;
    if (field.type === "TEXT_FIELD" || field.type === "REMARK") {
      return { customInputId: field.id, optionIds: [], textValue: "" };
    }
    return { customInputId: field.id, optionIds: [] };
  });

  // Entries for fields outside `fields` (NUMBERS / text / viewer-hidden) ride
  // along after the positional block, first occurrence per field, in order.
  const seen = new Set<number>();
  const tail = safeScopes.filter((s: any) => {
    const id = Number(s?.customInputId);
    if (!Number.isFinite(id) || fieldIds.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  return [...head, ...tail];
}

const sameIdList = (a: unknown, b: unknown) => {
  const x = Array.isArray(a) ? a.map(String) : [];
  const y = Array.isArray(b) ? b.map(String) : [];
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

/**
 * True when `current` already IS the aligned array — same slots, same field
 * per slot, same picks. The align effect only writes when this is false,
 * which is what keeps it loop-free while it watches the array.
 */
export function scopesEquivalent(current: any[], aligned: any[]): boolean {
  const cur = Array.isArray(current) ? current : [];
  if (cur.length !== aligned.length) return false;
  return aligned.every((row, i) => {
    const c = cur[i];
    if (!c || row == null) return c == null && row == null;
    return (
      Number(c.customInputId) === Number(row.customInputId) &&
      sameIdList(c.optionIds, row.optionIds) &&
      (c.textValue ?? undefined) === (row.textValue ?? undefined)
    );
  });
}
